/**
 * Controlador principal para el módulo de categorías.
 *
 * Este controlador maneja toda la lógica de negocio para la gestión de categorías,
 * incluyendo operaciones CRUD (Crear, Leer, Actualizar, Eliminar), validaciones de formularios,
 * paginación, filtrado y comunicación con servicios del backend. Proporciona una interfaz
 * completa para administrar categorías del sistema.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-05]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { useEffect, useState, useRef } from "react";
import Spinner from "components/MDSpinner";
import MDAlert from "components/Alert";
import { useNavigate } from "react-router-dom";
import FilterController from "components/filter/controller/FilterController";
import CategoryList from "../modules/CategoryList";
import { CategoryCreate } from "../modules/CategoryCreate";
import { CategoryEdit } from "../modules/CategoryEdit";
import { CategoryDelete } from "../modules/CategoryDelete";
import {
  CategoryCreateService,
  CategoryUpdateService,
  CategoryDeleteService,
} from "../services/CategoryService";

export default function CategoryController() {
  const filterConfig = [
    { visibleName: "Nombre", dbField: "name", type: "text" },
    { visibleName: "Creado", dbField: "created", type: "date" },
    { visibleName: "Modificado", dbField: "modified", type: "date" },
    {
      visibleName: "Creado por",
      dbField: "created_by_id",
      type: "text",
    },
    {
      visibleName: "Modificado por",
      dbField: "modified_by_id",
      type: "text",
    },
  ];

  const dataTable = "categories"; //Tabla indicada
  const filterControllerRef = useRef();
  //Variables de Crear categoría
  const [openCreate, setOpenCreate] = useState(false);
  //Variables de Editar categoría
  const [openEdit, setOpenEdit] = useState(false);
  //Variables de Eliminar categoría
  const [openDelete, setOpenDelete] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  // Estado de las validaciones
  const [isFieldValid, setIsFieldValid] = useState({
    name: true,
  });
  // Expresiones regulares para diferentes tipos de campos
  const validationPatterns = {
    name: /^[a-zA-ZÀ-ÿ\s\u00f1\u00d1,;.:()\-]+$/, // Permite letras, espacios y caracteres especiales comunes (coma, punto y coma, punto, dos puntos, paréntesis, guión)
  };

  // Variables de paginación
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesStart, setEntriesStart] = useState(0);
  const [entriesEnd, setEntriesEnd] = useState(0);
  const [paginaSize, setPaginaSize] = useState(10);

  // Variables de listado
  const [categoriesList, setCategoriesList] = useState([]);

  // Common Controllers
  const [isDisabled, setIsDisabled] = useState(false);
  const [defaultData, setDefaultData] = useState({});
  const [isFormModified, setIsFormModified] = useState(false);
  const [recoveredData, setRecoveredData] = useState({});
  const [editedData, setEditedData] = useState({});
  const [newData, setNewData] = useState({});
  const [buildData, setBuildData] = useState({});
  const [loading, setLoading] = useState(false);
  const [typeAlert, setTypeAlert] = useState("");
  const [message, setMessage] = useState("");
  const [isAlert, setIsAlert] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const header = [{}];

  /**
   * Cierra el modal de creación de categoría
   */
  const closeRoleCreate = () => setOpenCreate(false);

  /**
   * Cierra el modal de edición de categoría
   */
  const closeCategoryEdit = () => setOpenEdit(false);

  /**
   * Cierra el modal de eliminación de categoría
   */
  const closeCategoryDelete = () => setOpenDelete(false);

  /**
   * Obtiene el valor de un campo del formulario de creación
   * @param {string} fieldName - Nombre del campo
   * @returns {string} Valor del campo
   */
  const getFieldValueNew = (fieldName) => {
    return newData[fieldName];
  };

  /**
   * Maneja el cambio de valor en un campo del formulario de creación
   * @param {string} fieldName - Nombre del campo
   * @param {string} value - Nuevo valor del campo
   */
  const handleFieldChangeNew = (fieldName, value) => {
    setNewData({
      ...newData,
      [fieldName]: value,
    });
    handleChangeValid(fieldName, value);
  };

  /**
   * Obtiene el valor de un campo del formulario de edición
   * @param {string} fieldName - Nombre del campo
   * @returns {any} Valor del campo (editado o recuperado)
   */
  const getFieldValue = (fieldName) => {
    return editedData[fieldName] !== undefined ? editedData[fieldName] : recoveredData[fieldName];
  };

  /**
   * Maneja el cambio de valor en un campo del formulario de edición
   * @param {string} fieldName - Nombre del campo
   * @param {any} value - Nuevo valor del campo
   */
  const handleFieldChange = (fieldName, value) => {
    setEditedData({
      ...editedData,
      [fieldName]: value,
    });
    setIsFormModified(true);
    handleChangeValid(fieldName, value);
  };

  useEffect(() => {
    const hasFormChanged = Object.keys(editedData).some(
      (fieldName) => editedData[fieldName] !== recoveredData[fieldName]
    );

    const isAnyFieldEmpty = Object.values(editedData).some((value) => value === "");

    setIsFormModified(!isAnyFieldEmpty && hasFormChanged);
  }, [editedData]);

  /**
   * Maneja las alertas del sistema
   * @param {string} typeAlert - Tipo de alerta (success, fail, etc.)
   * @param {string} message - Mensaje de la alerta
   */
  const handleAlert = (typeAlert, message) => {
    setTypeAlert(typeAlert);
    setMessage(message);
    setIsAlert(true);
  };

  /**
   * Convierte todos los valores de un objeto a string
   * @param {Object} objeto - Objeto a convertir
   * @returns {Object} Objeto con valores convertidos a string
   */
  function convertValuesToString(objeto) {
    const convertedObject = {};

    for (const clave in objeto) {
      if (objeto.hasOwnProperty(clave)) {
        convertedObject[clave] = String(objeto[clave]);
      }
    }

    return convertedObject;
  }

  /**
   * Maneja el envío del formulario de creación de categoría
   * @param {Event} e - Evento del formulario
   */
  const submitCategoryCreate = async (e) => {
    e.preventDefault();
    const hasErrors = hasInvalidFields(isFieldValid);

    if (hasErrors) {
      handleAlert("fail", "Hay al menos un error en los campos ingresados");
      return;
    }

    setLoading(true);
    const convertedObject = convertValuesToString(newData);
    const result = await CategoryCreateService(convertedObject);

    if (result.success) {
      await filterControllerRef.current.handleClickSubmit();
      handleAlert("success", "La categoría se ha creado correctamente");
    } else {
      handleAlert("fail", result.message);
    }
    callHandleClickSubmit();
    closeRoleCreate();
    setLoading(false);
  };

  /**
   * Ejecuta la búsqueda/filtrado del controlador de filtros
   */
  const callHandleClickSubmit = () => {
    if (filterControllerRef.current) {
      filterControllerRef.current.handleClickSubmit();
    }
  };

  /**
   * Maneja la apertura del modal de creación de categoría
   * @param {Event} event - Evento del botón
   */
  const handleCategoryCreate = (event) => {
    event.preventDefault();
    resetFieldValid();
    setIsDisabled(false);
    const data = {
      name: "",
    };
    setNewData(data);
    setOpenCreate(true);
  };

  /**
   * Resetea las validaciones de todos los campos
   */
  const resetFieldValid = () => {
    setIsFieldValid({
      name: true,
    });
  };

  /**
   * Valida un campo contra su patrón de expresión regular
   * @param {string} value - Valor a validar
   * @param {RegExp} pattern - Patrón de validación
   * @returns {boolean} True si el valor es válido
   */
  const validateField = (value, pattern) => {
    return pattern.test(value);
  };

  /**
   * Maneja la validación de cambios en los campos
   * @param {string} fieldName - Nombre del campo
   * @param {string} value - Valor del campo
   */
  const handleChangeValid = (fieldName, value) => {
    const isFieldValid = validateField(value.trim(), validationPatterns[fieldName]);

    setIsFieldValid((prevValidity) => ({
      ...prevValidity,
      [fieldName]: isFieldValid,
    }));
  };

  /**
   * Verifica si hay campos inválidos en el formulario
   * @param {Object} fields - Objeto con el estado de validación de los campos
   * @returns {boolean} True si hay al menos un campo inválido
   */
  const hasInvalidFields = (fields) => {
    return Object.values(fields).some((value) => value === false);
  };

  /**
   * Maneja la apertura del modal de edición
   * @param {Object} category - Datos de la categoría a editar
   */
  const handleCategoryEdit = (category) => {
    resetFieldValid();
    setIsDisabled(false);
    setIsFormModified(false);
    setRecoveredData(category);
    setEditedData({});
    setOpenEdit(true);
  };

  /**
   * Envía los datos de edición al servidor
   * @param {Event} e - Evento del formulario
   */
  const submitCategoryEdit = async (e) => {
    e.preventDefault();
    const hasErrors = hasInvalidFields(isFieldValid);

    if (hasErrors) {
      handleAlert("fail", "Hay al menos un error en los campos ingresados");
      return;
    }

    setLoading(true);
    const convertedObject = convertValuesToString(editedData);
    const result = await CategoryUpdateService(recoveredData.id, convertedObject);

    if (result.success) {
      await filterControllerRef.current.handleClickSubmit();
      handleAlert("success", "La categoría se ha actualizado correctamente");
    } else {
      handleAlert("fail", result.message);
    }

    closeCategoryEdit();
    setLoading(false);
  };

  /**
   * Maneja la apertura del modal de eliminación
   * @param {Object} category - Datos de la categoría a eliminar
   */
  const handleCategoryDelete = (category) => {
    setCategoryToDelete(category);
    setOpenDelete(true);
  };

  /**
   * Confirma la eliminación de la categoría
   * @param {number} categoryId - ID de la categoría a eliminar
   */
  const confirmCategoryDelete = async (categoryId) => {
    setLoading(true);
    const result = await CategoryDeleteService(categoryId);

    if (result.success) {
      await filterControllerRef.current.handleClickSubmit();
      handleAlert("success", "La categoría se ha eliminado correctamente");
    } else {
      handleAlert("fail", result.message);
    }

    closeCategoryDelete();
    setCategoryToDelete(null);
    setLoading(false);
  };

  /**
   * Maneja el cambio de página en la paginación
   * @param {Event} event - Evento de paginación
   * @param {number} value - Número de página seleccionada
   */
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <>
      <Spinner loading={loading}></Spinner>
      <MDAlert
        isAlert={isAlert}
        setIsAlert={setIsAlert}
        typeAlert={typeAlert}
        message={message}></MDAlert>
      <FilterController
        filterConfig={filterConfig}
        dataTable={dataTable}
        ref={filterControllerRef}
        setTotalData={setTotalData}
        setEntriesEnd={setEntriesEnd}
        setEntriesStart={setEntriesStart}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        setTotalPages={setTotalPages}
        setListData={setCategoriesList}
        handleAlert={handleAlert}
      />
      <CategoryList
        handleCategoryCreate={handleCategoryCreate}
        handleCategoryEdit={handleCategoryEdit}
        handleCategoryDelete={handleCategoryDelete}
        categoriesList={categoriesList}
        setIsDisabled={setIsDisabled}
        isDisabled={isDisabled}
        filterControllerRef={filterControllerRef}
        entriesStart={entriesStart}
        entriesEnd={entriesEnd}
        totalData={totalData}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
      />
      <CategoryCreate
        open={openCreate}
        handleClose={closeRoleCreate}
        isFormModified={isFormModified}
        isDisabled={isDisabled}
        getFieldValue={getFieldValueNew}
        handleFieldChange={handleFieldChangeNew}
        handleSubmit={submitCategoryCreate}
        isFieldValid={isFieldValid}
      />
      <CategoryEdit
        open={openEdit}
        handleClose={closeCategoryEdit}
        datosRecuperados={recoveredData}
        datosEditados={editedData}
        isFormModified={isFormModified}
        isDisabled={isDisabled}
        getFieldValue={getFieldValue}
        handleFieldChange={handleFieldChange}
        handleSubmit={submitCategoryEdit}
        isFieldValid={isFieldValid}
      />
      <CategoryDelete
        open={openDelete}
        handleClose={closeCategoryDelete}
        handleDelete={confirmCategoryDelete}
        categoryData={categoryToDelete}
        isDisabled={isDisabled}
      />
    </>
  );
}
